import { AttendanceType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/attendance_type_pb'
import { EventType } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/event_type_pb'
import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import { MinusLargeIcon, PlusLargeIcon, TrashCanIcon } from '@fingertip/icons'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { MediaImage } from '@/components/blocks/shared/media-image'
import { EventTypeLocationBadge } from '@/components/booking/event-types/event-type-location-badge'
import { EventTypePriceBadge } from '@/components/booking/event-types/event-type-price-badge'
import { EventDurationBadge } from '@/components/booking/shared/event-duration-badge'
import { TooltipWrapper } from '@/components/shared/tooltip-wrapper'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useStoreConfigPricing } from '@/lib/hooks/use-store-config-pricing'
import { cn } from '@/lib/utils'

type Props = {
  eventType: EventType
  storeConfig?: StoreConfig
  count: number
  onSelect: (eventType: EventType) => void
  onDeselect: (eventType: EventType) => void
  onAdd: (eventType: EventType, quantity?: number) => void
  onRemove: (eventType: EventType, quantity?: number) => void
  showQuantityPicker?: boolean
  unavailable?: boolean
}

export const BookerEventTypeItem = ({
  eventType,
  storeConfig,
  onSelect,
  count,
  onAdd,
  onRemove,
  showQuantityPicker,
  unavailable,
}: Props) => {
  const { t } = useTranslation()
  const hasPrice = useMemo(() => {
    return eventType?.priceInCents || 0 > 0
  }, [eventType?.priceInCents])

  const breakpoint = useBreakpoint()

  const mediaSize = useMemo(() => {
    if (breakpoint === 'xs' || breakpoint === 'xxs') {
      return 100
    }
    return 140
  }, [breakpoint])

  const { feeDetails } = useStoreConfigPricing(storeConfig)

  return (
    <Card
      className={cn('p-4 flex flex-col gap-2', {
        'ring-ring ring-2': count > 0,
        'cursor-pointer hover:shadow-lg transition-all': !unavailable,
      })}
      onClick={() => {
        if (!showQuantityPicker) {
          onSelect(eventType)
        } else if (count === 0 && !unavailable) {
          onAdd(eventType)
        }
      }}
    >
      <div
        className={cn('flex flex-col gap-2', {
          'opacity-50': unavailable,
        })}
      >
        <div className="flex flex-row justify-between gap-4 md:gap-6">
          <div className="flex h-full flex-col items-start justify-between gap-4">
            <div className="flex flex-col gap-2">
              <div
                className="line-clamp-3 text-base font-medium md:text-xl"
                title={eventType.name}
              >
                <div
                  className="mr-2 inline-block size-3 max-h-3 min-w-3 rounded-full bg-card"
                  style={
                    eventType.color
                      ? { backgroundColor: eventType.color }
                      : {
                          border: '1px solid #E5E7EB',
                        }
                  }
                />
                <span>
                  {eventType.name}
                  {eventType?.attendanceType === AttendanceType.GROUP &&
                    ' (Group)'}
                  {eventType?.attendanceType === AttendanceType.EVENT &&
                    ' (Event)'}
                </span>
              </div>

              {/* Description */}
              {eventType?.description && (
                <p
                  className="line-clamp-3 text-xs md:line-clamp-4 md:text-sm"
                  dangerouslySetInnerHTML={{
                    __html: eventType.description.replace(/\n/g, '<br />'),
                  }}
                />
              )}
            </div>
          </div>

          {!!eventType.media && (
            <div>
              <MediaImage
                width={mediaSize}
                height={mediaSize}
                media={eventType.media}
                title={eventType.media.altText}
                className="overflow-hidden rounded-sm object-cover"
                style={{
                  width: mediaSize,
                  height: mediaSize,
                }}
                priority={true}
              />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {/* Badges */}
          <div className="flex flex-row items-center gap-2">
            {eventType?.length && (
              <EventDurationBadge duration={eventType.length} />
            )}

            {!!eventType && <EventTypeLocationBadge eventType={eventType} />}

            {eventType.priceInCents > 0 && (
              <EventTypePriceBadge
                eventType={eventType}
                storeConfig={storeConfig}
              />
            )}
          </div>

          {/* Fee details */}
          {hasPrice && feeDetails.length > 0 && (
            <div className="flex flex-row items-center">
              <div className="flex flex-col text-xs">
                {feeDetails
                  .map((fee) => {
                    return `${Math.floor(fee.percent)}%` + ' ' + fee.label
                  })
                  .join(', ')}
              </div>
            </div>
          )}
        </div>
      </div>

      {showQuantityPicker && (
        <div className="flex flex-row items-center justify-between">
          {!unavailable && (
            <div
              className="inline-flex flex-row items-center rounded-2xl border border-border bg-card p-1 shadow-xs dark:bg-accent"
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              {count === 0 ? (
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    onAdd(eventType)
                  }}
                  size="sm"
                  variant="ghost"
                  className="min-w-[112px]"
                >
                  {t('book')}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemove(eventType)
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    {count === 1 ? (
                      <TrashCanIcon className="size-4" />
                    ) : (
                      <MinusLargeIcon className="size-4" />
                    )}
                  </Button>

                  <span className="min-w-[32px] text-center">{count}</span>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      onAdd(eventType)
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <PlusLargeIcon className="size-4" />
                  </Button>
                </>
              )}
            </div>
          )}

          {unavailable && (
            <TooltipWrapper align="start" message={t('unavailable')}>
              <div className="flex flex-row items-center justify-end text-base text-muted-foreground/70 w-full">
                <span>{t('unavailable')}</span>
              </div>
            </TooltipWrapper>
          )}
        </div>
      )}
    </Card>
  )
}
